import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  Navigator,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type GridColumn,
  type NavNode,
} from '@/components/desktop';
import { CheckMenu } from '@/components/desktop/CheckMenu';
import { printTable } from '@/components/hrPrint';
import { ErrorNote, Field, Select, money, shortDate, todayIso } from '@/components/ui';
import { formatNumber } from '@/format';
import {
  employeeLabel,
  hrGet,
  monthName,
  taxYearLabel,
  taxYearOf,
  useEmployeeOptions,
  useFarmOptions,
} from '@/hooks/hr';
import { useFarmScope } from '@/stores/farmScope';
import './hr-admin.css';

type Need = 'farmId' | 'from' | 'to' | 'year' | 'month' | 'taxYear' | 'employeeId' | 'days';
type OtherNeed = Exclude<Need, 'farmId'>;

interface ReportInfo {
  name: string;
  title: string;
  needs: Need[];
  about: string;
}

interface ReportColumn {
  key: string;
  header: string;
  kind?: 'text' | 'money' | 'number' | 'date';
}

interface ReportResult {
  name: string;
  title: string;
  filename: string;
  columns: ReportColumn[];
  total: number;
  items: Array<Record<string, unknown>>;
  note?: string;
}

type Params = Record<OtherNeed, string>;

const STORED_NAME = 'poultry.hr.reports.name';

/** How the reports are grouped in the tree. */
const GROUPS: Array<{ id: string; label: string; icon: IconKind; reports: string[] }> = [
  { id: 'people', label: 'People', icon: 'people', reports: ['headcount', 'joiners-leavers'] },
  { id: 'attendance', label: 'Attendance & leave', icon: 'attendance', reports: ['attendance-summary', 'absentees', 'overtime', 'leave-balances'] },
  { id: 'money', label: 'Money', icon: 'payroll', reports: ['advances-owed', 'pay-changes', 'payroll-summary', 'labour-cost'] },
  { id: 'government', label: 'Government', icon: 'tax', reports: ['eobi', 'pessi', 'tax-deducted', 'tax-certificate', 'minimum-wage'] },
  { id: 'documents', label: 'Documents', icon: 'designation', reports: ['cnic-expiry'] },
];

function defaults(): Params {
  const today = todayIso();
  const [y, m] = today.split('-').map(Number);
  const lastYear = m === 1 ? y! - 1 : y!;
  const lastMonth = m === 1 ? 12 : m! - 1;
  return {
    from: `${today.slice(0, 7)}-01`,
    to: today,
    year: String(lastYear),
    month: String(lastMonth),
    taxYear: String(taxYearOf(today)),
    employeeId: '',
    days: '60',
  };
}

/** Only what the chosen report asks for goes to the server. */
function queryFor(report: ReportInfo | undefined, p: Params, farmId: string): Record<string, string> {
  const q: Record<string, string> = {};
  if (!report) return q;
  for (const need of report.needs) {
    if (need === 'farmId') {
      if (farmId) q.farmId = farmId;
    } else if (p[need]) {
      q[need] = p[need];
    }
  }
  return q;
}

function cellText(value: unknown, kind: ReportColumn['kind']): string {
  if (value === null || value === undefined || value === '') return kind === 'money' ? '—' : '';
  if (kind === 'money') return money(String(value));
  if (kind === 'number') return formatNumber(String(value));
  if (kind === 'date') return shortDate(String(value));
  return String(value);
}

/**
 * HR reports: pick one on the left, set what it needs, and Show. Each report
 * brings its own columns, so one grid shows any of them; each prints on the
 * letterhead. Pay figures are left out by the server for
 * anyone who cannot see pay.
 */
export function HrReports() {
  const [name, setName] = useState<string>(() => readStored(STORED_NAME, 'headcount'));
  const [params, setParams] = useState<Params>(defaults);
  const [submitted, setSubmitted] = useState<{ name: string; query: Record<string, string> } | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const catalogue = useQuery({
    queryKey: ['hr', 'reports', 'catalogue'],
    queryFn: () => hrGet<ReportInfo[]>('/api/v1/hr/reports'),
    staleTime: 5 * 60_000,
  });
  const reports = catalogue.data ?? [];
  const report = reports.find((r) => r.name === name) ?? reports[0];
  const needs = new Set(report?.needs ?? []);

  const farms = useFarmOptions();
  const current = useEmployeeOptions('CURRENT', needs.has('employeeId'));
  const leavers = useEmployeeOptions('LEFT', needs.has('employeeId'));

  // This screen's own farm filter holds what the top bar has until narrowed
  // here; changing it never moves the top bar, but changing the top bar
  // always replaces it — see `farmScope.ts`.
  const topFarmIds = useFarmScope((s) => s.selectedFarmIds);
  const allowedFarms = topFarmIds.length === 0 ? (farms.data ?? []) : (farms.data ?? []).filter((f) => topFarmIds.includes(f.id));
  const [farmSel, setFarmSel] = useState<string[]>(topFarmIds);
  const [headOffice, setHeadOffice] = useState(false);
  const prevTopFarmKey = useRef(topFarmIds.join(','));
  useEffect(() => {
    const key = topFarmIds.join(',');
    if (prevTopFarmKey.current !== key) {
      prevTopFarmKey.current = key;
      setFarmSel(topFarmIds);
    }
  }, [topFarmIds]);
  const farmIdParam = [...(headOffice ? ['HEAD_OFFICE'] : []), ...farmSel].join(',');

  const run = useQuery({
    queryKey: ['hr', 'reports', 'run', submitted?.name, submitted?.query],
    queryFn: () => hrGet<ReportResult>(`/api/v1/hr/reports/${submitted!.name}`, submitted!.query),
    enabled: submitted !== null && submitted.name === report?.name,
  });
  const result = submitted && report && submitted.name === report.name ? run.data : undefined;

  const nodes: NavNode[] = (() => {
    const known = new Set(GROUPS.flatMap((g) => g.reports));
    const groups = GROUPS.map((g) => ({
      id: `group:${g.id}`,
      label: g.label,
      icon: <AppIcon kind={g.icon} />,
      children: g.reports
        .map((n) => reports.find((r) => r.name === n))
        .filter((r): r is ReportInfo => r !== undefined)
        .map((r) => ({ id: r.name, label: r.title, hint: r.about, icon: <AppIcon kind="report" /> })),
    }));
    const other = reports.filter((r) => !known.has(r.name));
    if (other.length > 0) {
      groups.push({
        id: 'group:other',
        label: 'Other',
        icon: <AppIcon kind="report" />,
        children: other.map((r) => ({ id: r.name, label: r.title, hint: r.about, icon: <AppIcon kind="report" /> })),
      });
    }
    return groups.filter((g) => g.children.length > 0);
  })();

  function choose(id: string) {
    let next = id;
    if (id.startsWith('group:')) {
      const group = nodes.find((n) => n.id === id);
      next = group?.children?.[0]?.id ?? name;
    }
    setName(next);
    writeStored(STORED_NAME, next);
    setSelectedKey(null);
  }

  const set = (key: OtherNeed) => (value: string) => setParams((p) => ({ ...p, [key]: value }));
  const query = queryFor(report, params, farmIdParam);
  const missing = report?.needs.filter((n) => n !== 'farmId' && !params[n as OtherNeed]) ?? [];

  function show() {
    if (!report || missing.length > 0) return;
    setSelectedKey(null);
    setSubmitted({ name: report.name, query });
  }

  const shownQuery = result ? submitted!.query : query;
  const items = result?.items ?? [];
  // A pay column the server withheld from every row is left out, not shown blank.
  const columns = (result?.columns ?? []).filter(
    (c) => c.kind !== 'money' || items.length === 0 || items.some((r) => c.key in r),
  );
  const hiddenPay = (result?.columns ?? []).length > columns.length;
  const rows = items.map((r, i) => ({ ...r, __key: String(i) }));

  const gridColumns: Array<GridColumn<Record<string, unknown>>> = columns.map((c) => ({
    key: c.key,
    header: c.header,
    align: c.kind === 'money' || c.kind === 'number' ? ('end' as const) : ('start' as const),
    width: c.kind === 'money' ? 120 : c.kind === 'number' ? 100 : c.kind === 'date' ? 110 : 160,
    render: (r) => cellText(r[c.key], c.kind),
  }));

  // Totals of each money column; a row the report already totals itself is skipped.
  const totals = columns
    .filter((c) => c.kind === 'money')
    .map((c) => ({
      header: c.header,
      sum: items
        .filter((r) => !Object.values(r).includes('Total'))
        .reduce((t, r) => t + (r[c.key] === undefined || r[c.key] === null || r[c.key] === '' ? 0 : Number(r[c.key])), 0),
    }));

  const allFarmsTicked = farmSel.length === 0 || farmSel.length >= allowedFarms.length;
  const farmLabel = (() => {
    if (!headOffice && allFarmsTicked) return `All farms (${allowedFarms.length})`;
    const names = [
      ...(headOffice ? ['Head office'] : []),
      ...allowedFarms.filter((f) => farmSel.includes(f.id)).map((f) => f.nameEn),
    ];
    if (names.length <= 2) return names.join(', ');
    return `${names.length} farms`;
  })();

  function describe(q: Record<string, string>): string {
    const parts: string[] = [];
    if (report?.needs.includes('farmId')) parts.push(farmLabel);
    if (q.from && q.to) parts.push(`${shortDate(q.from)} to ${shortDate(q.to)}`);
    if (q.month && q.year) parts.push(`${monthName(Number(q.month))} ${q.year}`);
    else if (q.year) parts.push(q.year);
    if (q.taxYear) parts.push(`Tax year ${taxYearLabel(Number(q.taxYear))}`);
    if (q.days) parts.push(`Next ${q.days} days`);
    return parts.join(' · ');
  }

  function print() {
    if (!result) return;
    printTable({
      title: result.title,
      subtitle: [describe(shownQuery), result.note].filter(Boolean).join(' — '),
      columns: columns.map((c) => ({ key: c.key, header: c.header, kind: c.kind })),
      rows: items,
      landscape: columns.length > 6,
    });
  }

  const actions: DesktopAction[] = [
    {
      label: 'Print',
      icon: 'print',
      onSelect: print,
      enabled: result !== undefined,
      reason: 'Show the report first',
    },
  ];

  const now = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => now + 1 - i).map((y) => ({ value: String(y), label: String(y) }));
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: monthName(i + 1) }));
  const thisTaxYear = taxYearOf(todayIso());
  const taxYearOptions = Array.from({ length: 6 }, (_, i) => thisTaxYear + 1 - i).map((y) => ({ value: String(y), label: taxYearLabel(y) }));
  const people = [
    ...(current.data?.items ?? []).map((e) => ({ value: e.id, label: employeeLabel(e) })),
    ...(leavers.data?.items ?? []).map((e) => ({ value: e.id, label: `${employeeLabel(e)} — left` })),
  ];

  return (
    <div className="workbench">
      <SplitView
        id="hr-reports"
        initial={240}
        firstLabel="reports list"
        first={
          // Mounted once the list has arrived, so every group starts open the first time.
          nodes.length > 0 ? (
            <Navigator id="hr-reports" label="HR reports" nodes={nodes} selectedId={report?.name ?? ''} onSelect={choose} />
          ) : (
            <p className="muted hrsetup__page">{catalogue.isPending ? 'Loading reports…' : 'No reports.'}</p>
          )
        }
        second={
          <div className="workbench__main">
            {catalogue.error && <ErrorNote message={catalogue.error.message} />}
            {!report ? (
              <p className="muted">{catalogue.isPending ? 'Loading reports…' : 'No reports are available to you.'}</p>
            ) : (
              <>
                <div className="hrreport__head">
                  <h2 className="withicon">
                    <AppIcon kind="report" />
                    {report.title}
                  </h2>
                  <span className="muted">{report.about}</span>
                </div>
                <form
                  className="filterbar hrreport__params"
                  onSubmit={(e) => {
                    e.preventDefault();
                    show();
                  }}
                >
                  {needs.has('farmId') && (
                    <div className="field">
                      <span className="field__label">Farm</span>
                      <CheckMenu
                        wrapClassName="farmpicker"
                        menuClassName="farmpicker__menu"
                        menuLabel="Choose farms for this report"
                        trigger={(open, toggleOpen) => (
                          <button
                            type="button"
                            className="farmpicker__trigger"
                            aria-haspopup="true"
                            aria-expanded={open}
                            onClick={toggleOpen}
                            aria-label="Filter this report by farm"
                          >
                            <span>{farmLabel}</span>
                          </button>
                        )}
                      >
                        <label className="farmpicker__item">
                          <input
                            type="checkbox"
                            checked={headOffice}
                            onChange={(e) => setHeadOffice(e.target.checked)}
                          />
                          <span>Head office</span>
                        </label>
                        <label className="farmpicker__item">
                          <input
                            type="checkbox"
                            checked={!headOffice && allFarmsTicked}
                            onChange={() => {
                              setHeadOffice(false);
                              setFarmSel([]);
                            }}
                          />
                          <span>All farms ({allowedFarms.length})</span>
                        </label>
                        {allowedFarms.map((f) => (
                          <label key={f.id} className="farmpicker__item">
                            <input
                              type="checkbox"
                              checked={farmSel.includes(f.id)}
                              onChange={() =>
                                setFarmSel((sel) =>
                                  sel.includes(f.id) ? sel.filter((id) => id !== f.id) : [...sel, f.id],
                                )
                              }
                            />
                            <span>
                              {f.code} — {f.nameEn}
                            </span>
                          </label>
                        ))}
                      </CheckMenu>
                    </div>
                  )}
                  {needs.has('from') && <Field label="From" type="date" required value={params.from} onChange={set('from')} />}
                  {needs.has('to') && <Field label="To" type="date" required value={params.to} onChange={set('to')} />}
                  {needs.has('year') && (
                    <Select label="Year" required value={params.year} onChange={set('year')} options={yearOptions} />
                  )}
                  {needs.has('month') && (
                    <Select label="Month" required value={params.month} onChange={set('month')} options={monthOptions} />
                  )}
                  {needs.has('taxYear') && (
                    <Select label="Tax year" required value={params.taxYear} onChange={set('taxYear')} options={taxYearOptions} />
                  )}
                  {needs.has('employeeId') && (
                    <Select label="Person" required value={params.employeeId} onChange={set('employeeId')} options={people} />
                  )}
                  {needs.has('days') && (
                    <Field label="Within the next (days)" type="number" min={0} max={3650} value={params.days} onChange={set('days')} />
                  )}
                  <button
                    type="submit"
                    className="btn btn--primary btn--withicon"
                    disabled={missing.length > 0}
                    title={missing.length > 0 ? 'Fill in everything marked * first' : undefined}
                  >
                    <AppIcon kind="report" size={15} />
                    Show
                  </button>
                </form>
                <ActionBar actions={actions} />
                {run.error && submitted?.name === report.name && <ErrorNote message={run.error.message} />}
                {result?.note && <p className="hrreport__note">{result.note}</p>}
                {hiddenPay && (
                  <p className="hrreport__note">Pay figures are left out because you do not have permission to see pay.</p>
                )}
                {submitted?.name === report.name ? (
                  <DataGrid
                    id={`hr-report-${report.name}`}
                    label={report.title}
                    columns={gridColumns}
                    rows={rows}
                    rowKey={(r) => String(r.__key)}
                    selectedKey={selectedKey}
                    onSelect={setSelectedKey}
                    loading={run.isFetching}
                    empty="Nothing to show for these choices."
                  />
                ) : (
                  <p className="muted">Choose what you need above, then press Show.</p>
                )}
              </>
            )}
          </div>
        }
      />
      <StatusBar>
        <span>{result ? `${result.total} rows` : 'Not shown yet'}</span>
        {result && <span>{describe(shownQuery)}</span>}
        {result &&
          totals.map((t) => (
            <span key={t.header}>
              {t.header}: {money(String(t.sum))}
            </span>
          ))}
      </StatusBar>
    </div>
  );
}
